import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import LegalSection from './LegalSection';

export default function UserSettings() {
  const { user, logout } = useAuth();
  const toast = useToast();
  const { consent, openPreferences, policyVersion } = useCookieConsent();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [settings, setSettings] = useState({
    notifications: {
      emailNotifications: true,
      meetingReminders: true,
      chatNotifications: true
    },
    emailPreferences: {
      marketing: true,
      productUpdates: true,
      systemAnnouncements: true
    }
  });

  useEffect(() => {
    if (user) {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load email preferences from user document
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      // Load notification settings from subcollection
      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
      const settingsSnap = await getDoc(settingsRef);

      const userData = userSnap.exists() ? userSnap.data() : {};
      const settingsData = settingsSnap.exists() ? settingsSnap.data() : {};

      setSettings({
        notifications: {
          emailNotifications: settingsData.notifications?.emailNotifications ?? true,
          meetingReminders: settingsData.notifications?.meetingReminders ?? true,
          chatNotifications: settingsData.notifications?.chatNotifications ?? true
        },
        emailPreferences: {
          marketing: userData.emailPreferences?.marketing ?? true,
          productUpdates: userData.emailPreferences?.productUpdates ?? true,
          systemAnnouncements: userData.emailPreferences?.systemAnnouncements ?? true
        }
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);
    try {
      // Save emailPreferences to main user document
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        emailPreferences: {
          ...settings.emailPreferences,
          lastPreferenceUpdate: serverTimestamp()
        }
      }, { merge: true });

      // Save notification settings to subcollection
      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
      await setDoc(settingsRef, {
        notifications: settings.notifications,
        updatedAt: serverTimestamp()
      });

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    if (exporting) return;

    setExporting(true);
    try {
      // Gather user data from Firestore
      const userData = {
        profile: {},
        settings: {},
        recordings: [],
        meetings: []
      };

      // Get profile data
      const profileRef = doc(db, 'users', user.uid);
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        userData.profile = profileSnap.data();
      }

      // Get settings data
      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        userData.settings = settingsSnap.data();
      }

      // Create downloadable JSON file
      const dataStr = JSON.stringify(userData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `quickmeet-data-${user.uid}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (deleting) return;

    setDeleting(true);
    try {
      // Delete user data from Firestore
      const userRef = doc(db, 'users', user.uid);
      await deleteDoc(userRef);

      // Delete settings
      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
      await deleteDoc(settingsRef);

      // Delete the authentication account
      await deleteUser(user);

      toast.success('Account deleted successfully');

      // Logout will happen automatically when user is deleted
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Please log out and log back in, then try deleting your account again');
      } else {
        toast.error('Failed to delete account. Please contact support.');
      }
      setDeleting(false);
    }
  };

  const ToggleSwitch = ({ enabled, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-xs text-[#1d1d1f] dark:text-white">{label}</span>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          enabled ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]' : 'bg-[#d2d2d7] dark:bg-[#3c3c3e]'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#000000] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#06c] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#1d1d1f] dark:text-white font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl max-h-[85vh] overflow-y-auto">
      <div className="max-w-2xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white mb-1">
            Settings
          </h1>
          <p className="text-sm text-[#6e6e73] dark:text-[#86868b]">
            Manage your account preferences
          </p>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Notifications
          </h2>

          <div className="space-y-3">
            <ToggleSwitch
              enabled={settings.notifications.emailNotifications}
              onChange={(value) => setSettings(prev => ({
                ...prev,
                notifications: { ...prev.notifications, emailNotifications: value }
              }))}
              label="Email notifications"
            />
            <ToggleSwitch
              enabled={settings.notifications.meetingReminders}
              onChange={(value) => setSettings(prev => ({
                ...prev,
                notifications: { ...prev.notifications, meetingReminders: value }
              }))}
              label="Meeting reminders"
            />
            <ToggleSwitch
              enabled={settings.notifications.chatNotifications}
              onChange={(value) => setSettings(prev => ({
                ...prev,
                notifications: { ...prev.notifications, chatNotifications: value }
              }))}
              label="Chat notifications"
            />
          </div>
        </div>

        {/* Email Preferences */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2">
            Email Preferences
          </h2>
          <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mb-4">
            Control what emails you receive from QuickMeet
          </p>

          <div className="space-y-3">
            <ToggleSwitch
              enabled={settings.emailPreferences.marketing}
              onChange={(value) => setSettings(prev => ({
                ...prev,
                emailPreferences: { ...prev.emailPreferences, marketing: value }
              }))}
              label="Marketing & promotional emails"
            />
            <p className="text-xs text-[#86868b] dark:text-[#6e6e73] ml-0 -mt-1 mb-2">
              Special offers, campaigns, and announcements
            </p>

            <ToggleSwitch
              enabled={settings.emailPreferences.productUpdates}
              onChange={(value) => setSettings(prev => ({
                ...prev,
                emailPreferences: { ...prev.emailPreferences, productUpdates: value }
              }))}
              label="Product updates & tips"
            />
            <p className="text-xs text-[#86868b] dark:text-[#6e6e73] ml-0 -mt-1 mb-2">
              New features, tips, and best practices
            </p>

            <ToggleSwitch
              enabled={settings.emailPreferences.systemAnnouncements}
              onChange={(value) => setSettings(prev => ({
                ...prev,
                emailPreferences: { ...prev.emailPreferences, systemAnnouncements: value }
              }))}
              label="System announcements"
            />
            <p className="text-xs text-[#86868b] dark:text-[#6e6e73] ml-0 -mt-1">
              Important updates about platform changes
            </p>
          </div>

          <div className="mt-4 pt-4 border-t border-[#d2d2d7] dark:border-[#2c2c2e]">
            <p className="text-xs text-[#86868b] dark:text-[#6e6e73]">
              Note: Transactional emails (password resets, security alerts, etc.) cannot be disabled.
            </p>
          </div>
        </div>

        {/* Data & Privacy */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Data & Privacy
          </h2>

          <div className="space-y-3">
            {/* Export Data */}
            <div className="flex items-start justify-between p-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg">
              <div className="flex-1">
                <h3 className="font-medium text-[#1d1d1f] dark:text-white mb-0.5 text-sm">
                  Export Your Data
                </h3>
                <p className="text-xs text-[#6e6e73] dark:text-[#86868b]">
                  Download a copy of your account data
                </p>
              </div>
              <button
                onClick={handleExportData}
                disabled={exporting}
                className="ml-3 px-3 py-1.5 bg-white dark:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white border border-[#d2d2d7] dark:border-[#4c4c4e] rounded-lg font-medium hover:bg-[#f5f5f7] dark:hover:bg-[#4c4c4e] transition-all disabled:opacity-50 text-xs whitespace-nowrap"
              >
                {exporting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </span>
                ) : (
                  'Export'
                )}
              </button>
            </div>

            {/* Delete Account */}
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-medium text-red-600 dark:text-red-400 mb-0.5 text-sm">
                    Delete Account
                  </h3>
                  <p className="text-xs text-red-600/80 dark:text-red-400/80">
                    Permanently delete your account and all data
                  </p>
                </div>
              </div>

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full px-3 py-1.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all text-sm"
                >
                  Delete Account
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="p-2.5 bg-white dark:bg-[#1c1c1e] rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-xs text-[#1d1d1f] dark:text-white mb-1.5">
                      This action cannot be undone. Type <span className="font-bold">DELETE</span> to confirm:
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="w-full px-2.5 py-1.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className="flex-1 px-3 py-1.5 bg-[#d2d2d7] dark:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white rounded-lg font-medium hover:bg-[#c5c5c9] dark:hover:bg-[#4c4c4e] transition-all text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== 'DELETE' || deleting}
                      className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {deleting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </span>
                      ) : (
                        'Confirm Delete'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cookie Preferences */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2">
            Cookie Preferences
          </h2>
          <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mb-4">
            Manage how QuickMeet uses cookies and tracking
          </p>

          <div className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-sm font-medium text-[#1d1d1f] dark:text-white">
                    Current Settings
                  </h3>
                </div>

                {consent ? (
                  <div className="space-y-1.5 ml-7">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#34c759]"></span>
                      <span className="text-xs text-[#6e6e73] dark:text-[#98989d]">
                        Essential: Always enabled
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${consent.functional ? 'bg-[#34c759]' : 'bg-[#ff3b30]'}`}></span>
                      <span className="text-xs text-[#6e6e73] dark:text-[#98989d]">
                        Functional: {consent.functional ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${consent.analytics ? 'bg-[#34c759]' : 'bg-[#ff3b30]'}`}></span>
                      <span className="text-xs text-[#6e6e73] dark:text-[#98989d]">
                        Analytics: {consent.analytics ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#aeaeb2] mt-2">
                      Policy version: {policyVersion}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[#6e6e73] dark:text-[#98989d] ml-7">
                    No cookie preferences set
                  </p>
                )}
              </div>

              <button
                onClick={openPreferences}
                className="ml-4 px-4 py-2 bg-white dark:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white border border-[#d2d2d7] dark:border-[#4c4c4e] rounded-xl font-medium hover:bg-[#f5f5f7] dark:hover:bg-[#4c4c4e] transition-all text-sm"
              >
                Manage
              </button>
            </div>
          </div>

          <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-3">
            You can change your cookie preferences at any time.{' '}
            <a href="/privacy" className="text-[#06c] hover:underline">
              Learn more
            </a>
          </p>
        </div>

        {/* Legal Agreements */}
        <LegalSection />

        {/* Save Button */}
        <div className="flex justify-end sticky bottom-0 bg-white dark:bg-[#1c1c1e] py-3 -mx-6 px-6 border-t border-[#d2d2d7] dark:border-[#2c2c2e]">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-[#06c] to-[#0055d4] text-white px-6 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
